import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";

import Header from '../../Header';
import Sidebar from '../../Sidebar';
import Footer from '../../Footer';
import Spinner from '../../common/Spinner';
import { editApp, updateApp } from '../../../redux/actions/AppActions';
import { UserDropdown } from '../../../redux/actions/userActions';
import { APP_UPDATE_RESET } from "../../../redux/constants/AppConstants";
import { ToastObjects } from '../../../redux/actions/toastObject';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import IntegrationsSection from './IntegrationsSection';

const EditApp = ({ match }) => {
    const appId = match.params.id;
    const history = useHistory();
    const dispatch = useDispatch();

    const appList = useSelector((state) => state.appList);
    const organization = useSelector((state) => state?.organizationDetails?.organization);
    const userRole = organization?.user_role;
    const { apps } = appList;
    const users = useSelector((state) => state.userDropdown.users);
    const organizationId = organization?.organization_id;
    const appEdit = useSelector((state) => state.appEdit);
    const { app } = appEdit;
    const appUpdate = useSelector((state) => state.appUpdate);
    const { success: successUpdate } = appUpdate;

    const [submitted, setSubmitted] = useState(false);
    const [isLogo, setIsLogo] = useState(false);
    const [logo, setLogo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userIds, setUserIds] = useState([]);
    const [formState, setFormState] = useState({
        values: {
            isIos: 0,
            isAndriod: 0,
            bundleName: '',
            packageName: '',
            slack_webhook_url: '',
            isSlack: false,
        },
    });
    const [status, setStatus] = useState("");
    useEffect(() => {
        setFormState({ values: {} });
        if (successUpdate) {
            dispatch({ type: APP_UPDATE_RESET });
            toast.success("App updated successfully", ToastObjects);
        } else {
            if (!app.name || app.id !== Number(appId)) {
                dispatch(editApp(appId));
            } else {
                setFormState({ values: app });
                setSelectedUserIds(app.appAccess);
                setUserIds(app.appAccess);
            }
        }
        dispatch(UserDropdown('app', appId, organizationId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app, dispatch, appId, organizationId]);

    const handleUserChange = (event) => {
        const selectedUserId = parseInt(event.target.value);
        if (!selectedUserIds.includes(selectedUserId)) {
            setSelectedUserIds([...selectedUserIds, selectedUserId]);
        }
    };

    const removeUser = (userIdToRemove) => {
        setSelectedUserIds(selectedUserIds.filter((id) => id !== userIdToRemove));
    };

    const filteredOptions = users?.filter((option) => !selectedUserIds.includes(option.id)) || [];
    const selectedUsers = users?.filter((option) => selectedUserIds.includes(option.id)) || [];

    const handleChange = (event) => {
        setFormState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                [event.target.name]:
                    event.target.type === 'checkbox' ? event.target.checked : event.target.value,
            },
        }));
    };

    const handleOsToggle = (name) => (event) => {
        const checked = event.target.checked;
        setFormState((prev) => ({
            ...prev,
            values: { ...prev.values, [name]: checked ? 1 : 0 },
        }));
    };

    const handleUploadChange = (event) => {
        event.preventDefault();
        setLogo(event.target.files[0]);
    };

    const navigateToAppList = () => {
        history.push('/apps');
    };

    const handleStatus = (e) => {
        setStatus(e.target.value);
    };

    const handleToggleChange = () => {
        setFormState((prev) => ({
            ...prev,
            values: { ...prev.values, isSlack: !prev.values.isSlack },
        }));
    };

    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setSubmitted(true);
        const {
            name,
            description,
            isAndriod,
            isIos,
            packageName = '',
            bundleName = '',
            slack_webhook_url = '',
            isSlack = false,
        } = formState.values;
        const removeIds = userIds.filter((element) => !selectedUserIds.includes(element));

        if (!name || !description) return;

        if (!(isIos || isAndriod)) {
            toast.error("At least one OS must be selected");
            return;
        }

        let errorMessage = "";
        if (isAndriod === 1 && packageName.length === 0 && isIos === 0) {
            errorMessage = "Please enter App package name.";
        } else if (isIos === 1 && bundleName.length === 0 && isAndriod === 0) {
            errorMessage = "Please enter App Bundle name.";
        } else if (isAndriod === 1 && isIos === 1) {
            if (bundleName.length === 0 && packageName.length === 0) {
                errorMessage = "Please enter App Bundle name and App package name.";
            } else if (packageName.length === 0) {
                errorMessage = "Please enter App package name.";
            } else if (bundleName.length === 0) {
                errorMessage = "Please enter App Bundle name.";
            }
        }

        if (errorMessage) {
            toast.error(errorMessage);
            return;
        }

        setLoading(true);
        const payload = new FormData();
        payload.set("id", appId);
        payload.set("name", name);
        payload.set("description", description);
        payload.set("isAndriod", isAndriod);
        payload.set("isIos", isIos);
        payload.set("removeIds", removeIds);
        payload.set("selectedUserIds", selectedUserIds);
        payload.set("packageName", packageName);
        payload.set("bundleName", bundleName);
        payload.set("slack_webhook_url", slack_webhook_url);
        payload.set("isSlack", isSlack);

        if (logo) {
            payload.set("oldLogoId", app.logo_id);
            payload.append("logo", logo);
        }

        if (app.app_status === 'Requested') {
            payload.set("app_status", status);
        }

        dispatch(updateApp(payload)).then(() => {
            setSubmitted(false);
            setLoading(false);
            navigateToAppList();
        });
    };

    const isAnyRequested = app?.app_status === "Requested";
    const isIosChecked = formState.values.isIos === 1 || formState.values.isIos === true;
    const isAndroidChecked =
        formState.values.isAndriod === 1 || formState.values.isAndriod === true;
    const slackOn =
        formState.values.isSlack === 1 || formState.values.isSlack === true;
    const readOnly = isAnyRequested;

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                        <IconButton onClick={navigateToAppList} size="small" aria-label="Back">
                                            <ArrowBackIcon />
                                        </IconButton>
                                        <Typography variant="h3" component="h1" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                            {isAnyRequested ? 'App details' : 'Edit app'}
                                        </Typography>
                                    </Stack>

                                    <Box component="form" onSubmit={handleSubmit}>
                                        <Stack spacing={2.5}>
                                            <TextField
                                                label="App name"
                                                name="name"
                                                required
                                                fullWidth
                                                value={formState.values.name || ''}
                                                onChange={handleChange}
                                                InputProps={{ readOnly }}
                                                error={submitted && !formState.values.name}
                                                helperText={
                                                    submitted && !formState.values.name ? 'App name is required' : ''
                                                }
                                            />

                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}
                                                >
                                                    App logo *
                                                </Typography>
                                                {formState.values.logo_url && !isLogo ? (
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Avatar
                                                            alt="logo"
                                                            src={formState.values.logo_url}
                                                            variant="rounded"
                                                            sx={{ width: 80, height: 80 }}
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            type="button"
                                                            disabled={readOnly}
                                                            onClick={() => setIsLogo(true)}
                                                        >
                                                            Change logo
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Button
                                                        component="label"
                                                        variant="outlined"
                                                        startIcon={<UploadFileIcon />}
                                                        disabled={readOnly}
                                                    >
                                                        {logo ? logo.name : 'Choose logo'}
                                                        <input
                                                            type="file"
                                                            hidden
                                                            accept="image/*"
                                                            onChange={handleUploadChange}
                                                        />
                                                    </Button>
                                                )}
                                                {submitted && !logo && !formState.values.logo_url && (
                                                    <FormHelperText error>Logo is required</FormHelperText>
                                                )}
                                            </Box>

                                            <TextField
                                                label="Description"
                                                name="description"
                                                required
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={formState.values.description || ''}
                                                onChange={handleChange}
                                                InputProps={{ readOnly }}
                                                error={submitted && !formState.values.description}
                                                helperText={
                                                    submitted && !formState.values.description
                                                        ? 'Description is required'
                                                        : ''
                                                }
                                            />

                                            <FormControl
                                                component="fieldset"
                                                error={
                                                    submitted &&
                                                    !(formState.values.isIos || formState.values.isAndriod)
                                                }
                                            >
                                                <FormLabel sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                                    OS *
                                                </FormLabel>
                                                <Stack spacing={1.5}>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isIosChecked}
                                                                    onChange={handleOsToggle('isIos')}
                                                                    disabled={readOnly}
                                                                />
                                                            }
                                                            label={
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <AppleIcon fontSize="small" />
                                                                    <span>iOS</span>
                                                                </Stack>
                                                            }
                                                        />
                                                        {isIosChecked && (
                                                            <TextField
                                                                size="small"
                                                                name="bundleName"
                                                                placeholder="Enter Bundle ID"
                                                                value={formState.values.bundleName || ''}
                                                                onChange={handleChange}
                                                                InputProps={{ readOnly }}
                                                                sx={{ minWidth: 260 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isAndroidChecked}
                                                                    onChange={handleOsToggle('isAndriod')}
                                                                    disabled={readOnly}
                                                                />
                                                            }
                                                            label={
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <AndroidIcon fontSize="small" />
                                                                    <span>Android</span>
                                                                </Stack>
                                                            }
                                                        />
                                                        {isAndroidChecked && (
                                                            <TextField
                                                                size="small"
                                                                name="packageName"
                                                                placeholder="Enter Package Name"
                                                                value={formState.values.packageName || ''}
                                                                onChange={handleChange}
                                                                InputProps={{ readOnly }}
                                                                sx={{ minWidth: 260 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </Stack>
                                                {submitted &&
                                                    !(formState.values.isIos || formState.values.isAndriod) && (
                                                        <FormHelperText>At least one platform must be selected</FormHelperText>
                                                    )}
                                            </FormControl>

                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}
                                                >
                                                    Access to
                                                </Typography>
                                                {selectedUsers.length > 0 && (
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                                        {selectedUsers.map((u) => (
                                                            <Chip
                                                                key={u.id}
                                                                label={u.name}
                                                                onDelete={readOnly ? undefined : () => removeUser(u.id)}
                                                                size="small"
                                                            />
                                                        ))}
                                                    </Stack>
                                                )}
                                                <FormControl fullWidth size="small" disabled={readOnly}>
                                                    <InputLabel>Add user</InputLabel>
                                                    <Select label="Add user" value="" onChange={handleUserChange}>
                                                        {filteredOptions.map((u) => (
                                                            <MenuItem key={u.id} value={u.id}>
                                                                {u.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>

                                            {isAnyRequested && userRole === 'Admin' && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status *</InputLabel>
                                                    <Select
                                                        label="Status *"
                                                        value={status}
                                                        onChange={handleStatus}
                                                    >
                                                        <MenuItem value="">Select status</MenuItem>
                                                        {app.app_status === 'Requested' && [
                                                            <MenuItem key="a" value="Active">Approve</MenuItem>,
                                                            <MenuItem key="r" value="Rejected">Reject</MenuItem>,
                                                        ]}
                                                        {app.app_status === 'Active' && (
                                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                                        )}
                                                        {app.app_status === 'Inactive' && (
                                                            <MenuItem value="Active">Active</MenuItem>
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Stack>
                                    </Box>
                                </Paper>

                                {!isAnyRequested && (
                                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                                        <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
                                            Notifications
                                        </Typography>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={slackOn}
                                                        onChange={handleToggleChange}
                                                    />
                                                }
                                                label="Slack"
                                            />
                                            {slackOn && (
                                                <TextField
                                                    label="Webhook URL"
                                                    name="slack_webhook_url"
                                                    fullWidth
                                                    value={formState.values.slack_webhook_url || ''}
                                                    onChange={handleChange}
                                                    error={submitted && !formState.values.slack_webhook_url}
                                                />
                                            )}
                                        </Stack>
                                    </Paper>
                                )}

                                {!isAnyRequested && (
                                    <IntegrationsSection appId={formState.values.id} />
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button variant="outlined" onClick={navigateToAppList}>
                                        Cancel
                                    </Button>
                                    <Button variant="contained" onClick={() => handleSubmit()}>
                                        Save changes
                                    </Button>
                                </Stack>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
            {loading && <Spinner fullscreen label="Saving…" />}
        </>
    );
};

export default EditApp;
